import type { ContextPart, ExchangeFile, FieldOptions } from '@/api/types'

export const CONTEXT_PARTS: ContextPart[] = ['format', 'instructions', 'vocabulary', 'catalog']

export interface ContextSources {
  format: string | undefined
  instructions: string
  vocabulary: FieldOptions | undefined
  catalog: ExchangeFile | undefined
}

/** Each part as it is pasted, or null when it has nothing to say. */
export function renderPart(part: ContextPart, sources: ContextSources): string | null {
  switch (part) {
    case 'format':
      return sources.format?.trim() || null
    case 'instructions': {
      const text = sources.instructions.trim()
      return text
        ? '## The user\'s standing instructions\n\n' +
            'Follow these unless they conflict with the format rules.\n\n' +
            text
        : null
    }
    case 'vocabulary': {
      const vocabulary = sources.vocabulary
      if (!vocabulary) return null
      const list = (values: string[]) => (values.length ? values.join(', ') : '(none yet)')
      return (
        '## Vocabulary already in use\n\n' +
        'Use these values for `cuisine`, `protein_base` and `tags` where they fit. ' +
        'Only introduce a new one when nothing fits.\n\n' +
        `- Cuisines: ${list(vocabulary.cuisines)}\n` +
        `- Protein bases: ${list(vocabulary.protein_bases)}\n` +
        `- Tags: ${list(vocabulary.tags)}`
      )
    }
    case 'catalog':
      return sources.catalog
        ? '## Ingredient catalog\n\n' +
            'Every ingredient already in the library, with macros per 100 g.\n\n' +
            '```json\n' +
            JSON.stringify(sources.catalog, null, 2) +
            '\n```'
        : null
  }
}

/** The package as pasted: the included parts that have content, in a fixed order. */
export function buildContext(sources: ContextSources, excluded: ContextPart[]): string {
  return (
    CONTEXT_PARTS.filter((part) => !excluded.includes(part))
      .map((part) => renderPart(part, sources))
      .filter((text): text is string => text !== null)
      .join('\n\n') + '\n'
  )
}
